import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, User as UserIcon, Sparkles, Loader2, ArrowLeft } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'sa-chat-session';
const OPEN_KEY = 'sa-chat-opened-once';

// Floating boutique-style AI chat assistant. Sits in the bottom-right
// corner of every public page. On first click it greets the visitor and
// proposes quick-reply chips; the visitor can either keep chatting with
// Aria (the AI) or hand over to a human by dropping their coordinates.
export const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return window.localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [chips, setChips] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState('chat'); // 'chat' | 'handover' | 'handover-done'
  const [contact, setContact] = useState({ name: '', email: '', phone: '', question: '' });
  const [submittingContact, setSubmittingContact] = useState(false);
  const [pulse, setPulse] = useState(false);
  const bodyRef = useRef(null);

  // Hide the chat on admin / authenticated app surfaces — it's a public
  // visitor tool. Detection is path-based for simplicity.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const HIDE_ON = ['/admin', '/mes-formations', '/mon-dossier', '/connexion', '/inscription', '/mot-de-passe-oublie'];
  const shouldHide = HIDE_ON.some((p) => path.includes(p));

  // Auto-pulse the bubble once per visitor to draw the eye.
  useEffect(() => {
    if (shouldHide) return;
    try {
      const seen = window.localStorage.getItem(OPEN_KEY);
      if (!seen) {
        const t = setTimeout(() => setPulse(true), 3500);
        return () => clearTimeout(t);
      }
    } catch { /* noop */ }
  }, [shouldHide]);

  // Restore previous conversation (if any) the first time the panel opens
  const loadSession = useCallback(async (sid) => {
    try {
      const res = await axios.get(`${API_URL}/chat/session/${sid}`);
      setMessages(res.data.messages || []);
      setChips(res.data.chips || []);
    } catch {
      // Fallback initial greeting
      setMessages([{
        role: 'assistant',
        content: "Salut ! Moi c'est Aria, l'assistante IA de la Sellerie Académie. Je peux t'aider sur les formations, les financements, le métier de sellier ou le processus d'inscription. Dis-moi ce qui t'intéresse !",
        ts: new Date().toISOString(),
      }]);
      setChips([
        "Quelles formations proposez-vous ?",
        "Quel est le prix d'une formation ?",
        "Comment se finance ma formation ?",
        "Quand est la prochaine session ?",
      ]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    try { window.localStorage.setItem(OPEN_KEY, '1'); } catch { /* noop */ }
    setPulse(false);
    if (sessionId && messages.length === 0) loadSession(sessionId);
    if (!sessionId && messages.length === 0) loadSession('bootstrap'); // returns greeting+chips
  }, [open, sessionId, messages.length, loadSession]);

  useEffect(() => {
    // auto-scroll to bottom on new message
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, sending, mode]);

  const send = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    setInput('');
    setChips([]);
    setMessages((m) => [...m, { role: 'user', content: trimmed, ts: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/chat/message`, {
        session_id: sessionId || undefined,
        message: trimmed,
      });
      if (!sessionId && res.data.session_id) {
        setSessionId(res.data.session_id);
        try { window.localStorage.setItem(STORAGE_KEY, res.data.session_id); } catch { /* noop */ }
      }
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply, ts: new Date().toISOString() }]);
    } catch {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: "Désolée, j'ai un petit souci. Tu peux retenter ou demander à parler à un humain ci-dessous.",
        ts: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const submitHandover = async () => {
    if (!contact.name.trim() || !contact.email.trim()) return;
    setSubmittingContact(true);
    try {
      await axios.post(`${API_URL}/chat/handover`, {
        session_id: sessionId || undefined,
        ...contact,
      });
      setMode('handover-done');
    } catch (err) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: "Hmm, je n'ai pas pu transmettre ta demande. Réessaie ou écris-nous directement à assistance.sellerie@gmail.com.",
        ts: new Date().toISOString(),
      }]);
      setMode('chat');
    } finally {
      setSubmittingContact(false);
    }
  };

  if (shouldHide) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-[60]">
          {pulse && (
            <span
              className="absolute inset-0 rounded-full bg-[#C9B896]/40 animate-ping pointer-events-none"
              aria-hidden="true"
            />
          )}
          <button
            type="button"
            aria-label="Ouvrir le chat avec l'assistante IA"
            onClick={() => setOpen(true)}
            className="relative w-14 h-14 rounded-full bg-[#1F2A4A] text-[#FAF7F0] shadow-[0_18px_45px_-12px_rgba(31,42,74,0.5)] flex items-center justify-center hover:bg-[#324166] hover:scale-105 transition-all ring-2 ring-[#C9B896]/40"
            data-testid="chat-widget-toggle"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#C9B896] border-2 border-[#1F2A4A]" />
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] max-w-[380px] h-[min(620px,calc(100vh-2.5rem))] bg-[#FAF7F0] rounded-2xl shadow-[0_30px_80px_-15px_rgba(15,22,35,0.45)] border border-[#1F2A4A]/15 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-[#1F2A4A] text-[#FAF7F0] px-4 pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9B896] to-[#A09472] flex items-center justify-center shrink-0 ring-2 ring-[#FAF7F0]/30">
                  <Sparkles className="w-4 h-4 text-[#0F1623]" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm leading-tight flex items-center gap-1.5">
                    Aria
                    <span className="inline-flex items-center gap-1 px-1.5 py-0 rounded-full bg-[#C9B896]/20 text-[#C9B896] text-[9px] uppercase tracking-wider font-mono">
                      <Sparkles className="w-2 h-2" /> IA
                    </span>
                  </p>
                  <p className="text-[11px] text-white/70 truncate">
                    Assistante de la Sellerie Académie
                  </p>
                </div>
              </div>
              <button
                type="button"
                aria-label="Fermer le chat"
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white p-1"
                data-testid="chat-widget-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {mode === 'chat' && (
              <button
                type="button"
                onClick={() => setMode('handover')}
                className="mt-3 w-full bg-[#FAF7F0]/10 hover:bg-[#FAF7F0]/15 text-[#C9B896] text-xs font-medium py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                data-testid="chat-widget-handover-open"
              >
                <UserIcon className="w-3.5 h-3.5" />
                Parler à un humain
              </button>
            )}
            {mode === 'handover' && (
              <button
                type="button"
                onClick={() => setMode('chat')}
                className="mt-3 inline-flex items-center gap-1 text-xs text-[#C9B896] hover:text-white transition-colors"
                data-testid="chat-widget-back-chat"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Revenir au chat
              </button>
            )}
          </div>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-widget-body">
            {mode === 'chat' && (
              <>
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}
                  >
                    {m.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9B896] to-[#A09472] flex items-center justify-center shrink-0 mt-0.5">
                        <Sparkles className="w-3 h-3 text-[#0F1623]" />
                      </div>
                    )}
                    <div
                      className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-[#1F2A4A] text-[#FAF7F0] rounded-br-sm'
                          : 'bg-white text-[#1F2A4A] border border-[#1F2A4A]/10 rounded-bl-sm'
                      }`}
                      data-testid={`chat-msg-${m.role}-${i}`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9B896] to-[#A09472] flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-3 h-3 text-[#0F1623]" />
                    </div>
                    <div className="bg-white text-[#1F2A4A]/60 border border-[#1F2A4A]/10 rounded-2xl rounded-bl-sm px-3.5 py-2.5 inline-flex gap-1 items-center text-sm" data-testid="chat-typing-indicator">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9B896] animate-pulse" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9B896] animate-pulse [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9B896] animate-pulse [animation-delay:300ms]" />
                    </div>
                  </div>
                )}
                {chips.length > 0 && !sending && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-chips">
                    {chips.map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => send(c)}
                        className="text-xs bg-[#C9B896]/15 hover:bg-[#C9B896]/30 text-[#1F2A4A] border border-[#C9B896]/40 rounded-full px-3 py-1.5 transition-colors"
                        data-testid={`chat-chip-${i}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {mode === 'handover' && (
              <div className="space-y-3" data-testid="chat-handover-form">
                <p className="text-sm text-[#1F2A4A]/80 leading-relaxed">
                  Laisse-nous tes coordonnées et un membre de l'équipe te recontacte sous 48 h.
                </p>
                <div>
                  <label className="text-xs font-medium text-[#1F2A4A] mb-1 block">Prénom & nom *</label>
                  <input
                    value={contact.name}
                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    placeholder="ex. Marie Dupont"
                    className="w-full bg-white text-[#1F2A4A] placeholder:text-[#1F2A4A]/40 border border-[#1F2A4A]/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9B896]"
                    data-testid="chat-handover-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1F2A4A] mb-1 block">Email *</label>
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder="marie@email.com"
                    className="w-full bg-white text-[#1F2A4A] placeholder:text-[#1F2A4A]/40 border border-[#1F2A4A]/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9B896]"
                    data-testid="chat-handover-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1F2A4A] mb-1 block">Téléphone (optionnel)</label>
                  <input
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    placeholder="+33 …"
                    className="w-full bg-white text-[#1F2A4A] placeholder:text-[#1F2A4A]/40 border border-[#1F2A4A]/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9B896]"
                    data-testid="chat-handover-phone"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1F2A4A] mb-1 block">Ta question</label>
                  <textarea
                    rows={3}
                    value={contact.question}
                    onChange={(e) => setContact({ ...contact, question: e.target.value })}
                    placeholder="Décris ton projet ou ta demande…"
                    className="w-full bg-white text-[#1F2A4A] placeholder:text-[#1F2A4A]/40 border border-[#1F2A4A]/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9B896] resize-none"
                    data-testid="chat-handover-question"
                  />
                </div>
                <button
                  type="button"
                  disabled={submittingContact || !contact.name.trim() || !contact.email.trim()}
                  onClick={submitHandover}
                  className="w-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="chat-handover-submit"
                >
                  {submittingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Envoyer mes coordonnées
                </button>
                <p className="text-[10px] text-[#1F2A4A]/55 leading-snug">
                  Tes informations sont uniquement utilisées pour te recontacter et ne sont pas partagées.
                </p>
              </div>
            )}

            {mode === 'handover-done' && (
              <div className="text-center py-8 px-2" data-testid="chat-handover-done">
                <div className="w-12 h-12 rounded-full bg-[#C9B896] mx-auto mb-4 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#0F1623]" />
                </div>
                <h3 className="font-serif italic text-xl text-[#1F2A4A] mb-2">Merci&nbsp;!</h3>
                <p className="text-sm text-[#1F2A4A]/80 max-w-xs mx-auto">
                  On revient vers toi sous 48 h. En attendant, Aria reste là si tu as d'autres questions.
                </p>
                <button
                  type="button"
                  onClick={() => setMode('chat')}
                  className="mt-5 text-xs text-[#C9B896] hover:text-[#1F2A4A] underline underline-offset-4"
                  data-testid="chat-handover-back"
                >
                  Continuer à discuter avec Aria
                </button>
              </div>
            )}
          </div>

          {/* Input */}
          {mode === 'chat' && (
            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="border-t border-[#1F2A4A]/10 bg-white p-2.5 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Pose ta question à Aria…"
                disabled={sending}
                className="flex-1 bg-transparent text-sm text-[#1F2A4A] placeholder:text-[#1F2A4A]/40 px-2 py-2 focus:outline-none disabled:opacity-50"
                data-testid="chat-widget-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="w-9 h-9 rounded-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                aria-label="Envoyer"
                data-testid="chat-widget-send"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default ChatAssistant;
